import React from "react";
import { UploadCloud, ChevronDown, X, Image as ImageIcon, Video } from "lucide-react";

export function Button({ children, variant = "primary", className = "", ...props }) {
  const baseStyle = "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-grigora-500 hover:bg-grigora-600 text-white shadow-sm hover:shadow focus:ring-grigora-500 border border-transparent",
    secondary: "bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 shadow-sm hover:border-slate-300 focus:ring-slate-200",
    ghost: "bg-transparent hover:bg-slate-100 text-slate-600 hover:text-slate-900 focus:ring-slate-200",
    danger: "bg-white hover:bg-red-50 text-red-600 border border-red-200 shadow-sm focus:ring-red-500",
  };
  return (
    <button className={`${baseStyle} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
}

export function Card({ children, className = "", noPadding = false, onClick, fillHeight = false }) {
  const innerClass = noPadding ? "" : "p-6";
  const innerFillClass = fillHeight ? " h-full min-h-0 flex flex-col" : "";
  return (
    <div
      onClick={onClick}
      className={`bg-white border border-slate-200 rounded-xl shadow-card hover:shadow-md transition-all duration-200 ${onClick ? "cursor-pointer" : ""} ${className}`}
    >
      <div className={`${innerClass}${innerFillClass}`}>{children}</div>
    </div>
  );
}

export function Skeleton({ className = "", ...props }) {
  return <div className={`animate-pulse rounded bg-slate-200 ${className}`} role="presentation" aria-hidden="true" {...props} />;
}

export function Input({ label, error, subtext, className = "", compact, ...props }) {
  return (
    <div className={compact ? "mb-3" : "mb-4"}>
      {label && <label className="block text-slate-700 text-xs font-semibold mb-1.5 capitalize">{label}</label>}
      <input
        className={`w-full bg-white border text-slate-900 placeholder:text-slate-400 focus:outline-none transition-all duration-200 ${compact
            ? `h-[36px] max-h-[36px] px-2.5 py-0 text-sm leading-none rounded-md hover:border-slate-300 focus:border-[#623CE7] focus:shadow-[0_0_0_2px_rgba(98,60,231,0.1)] box-border ${error ? "border-red-500" : "border-slate-200"}`
            : `rounded-lg ${error ? "border-red-500" : "border-slate-200"} focus:border-grigora-500 focus:ring-4 focus:ring-grigora-500/10 shadow-sm px-3 py-2.5`
          } ${className}`}
        {...props}
      />
      {subtext && <p className="text-slate-400 text-xs mt-1.5">{subtext}</p>}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

export function TextArea({ label, error, subtext, className = "", ...props }) {
  return (
    <div className="mb-4">
      {label && <label className="block text-slate-700 text-xs font-semibold mb-2 capitalize">{label}</label>}
      <textarea
        className={`w-full bg-white border ${error ? "border-red-500" : "border-slate-200"} rounded-lg px-3 py-2.5 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-grigora-500 focus:ring-4 focus:ring-grigora-500/10 transition-all duration-200 shadow-sm min-h-[120px] resize-y ${className}`}
        {...props}
      />
      {subtext && <p className="text-slate-400 text-xs mt-1.5">{subtext}</p>}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

export function Select({ label, error, options = [], className = "", ...props }) {
  return (
    <div className={className}>
      {label && <label className="block text-slate-700 text-xs font-semibold mb-2 capitalize">{label}</label>}
      <select
        className={`w-full bg-white border ${error ? "border-red-500" : "border-slate-200"} rounded-lg px-3 py-2.5 text-slate-900 text-sm focus:outline-none focus:border-grigora-500 focus:ring-4 focus:ring-grigora-500/10 transition-all duration-200 shadow-sm`}
        {...props}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

/** Compact select – matches grigora select-compact-antd: h-7, 10px text, rounded, slate border, chevron right. */
export function SelectCompact({ label, error, options = [], className = "", ...props }) {
  return (
    <div className={className}>
      {label && <label className="block text-slate-700 text-xs font-semibold mb-1.5">{label}</label>}
      <div className="relative w-full">
        <select
          className={`w-full h-7 bg-white border ${error ? "border-red-500" : "border-slate-200"} rounded pl-2.5 pr-6 text-[10px] text-left text-slate-700 focus:outline-none focus:border-grigora-500 focus:ring-2 focus:ring-grigora-500/10 hover:border-slate-300 transition-all appearance-none cursor-pointer min-w-0`}
          {...props}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 block" style={{ margin: 0, padding: 0, lineHeight: 1 }} />
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

export function Badge({ children, variant = "neutral" }) {
  const variants = {
    success: "bg-emerald-50 text-emerald-700 border-emerald-200/50",
    warning: "bg-amber-50 text-amber-700 border-amber-200/50",
    info: "bg-blue-50 text-blue-700 border-blue-200/50",
    neutral: "bg-slate-100 text-slate-600 border-slate-200/50",
    purple: "bg-grigora-50 text-grigora-700 border-grigora-200/50",
  };
  return (
    <span className={`px-2.5 py-1 rounded-md text-xs border ${variants[variant]} font-medium inline-flex items-center`}>
      {children}
    </span>
  );
}

export function CodeBlock({ code, language = "javascript" }) {
  return (
    <div className="relative group rounded-lg overflow-hidden border border-slate-200 shadow-sm">
      <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex justify-between items-center">
        <span className="text-xs font-mono text-slate-500">{language}</span>
        <button className="text-xs text-slate-500 hover:text-slate-900 font-medium" onClick={() => navigator.clipboard.writeText(code)}>
          Copy
        </button>
      </div>
      <div className="bg-white p-4 font-mono text-sm overflow-x-auto text-slate-700">
        <pre>{code}</pre>
      </div>
    </div>
  );
}

export function IconUpload({ label, value, onUpload, disabled, compact }) {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleClick = () => {
    if (disabled || uploading || !onUpload) return;
    inputRef.current?.click();
  };

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onUpload) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/webp"];
    if (!allowed.includes(file.type)) {
      setError("Use PNG, JPG, SVG or WebP.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("File must be under 2MB.");
      return;
    }
    setError("");
    setUploading(true);
    try {
      await onUpload(file);
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  if (compact) {
    return (
      <div className="h-full min-h-[3.5rem] w-full flex flex-col mb-0">
        {label && <label className="block text-slate-700 text-xs font-semibold mb-1 capitalize">{label}</label>}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
          className="hidden"
          onChange={handleChange}
        />
        <div
          role="button"
          tabIndex={onUpload && !disabled ? 0 : -1}
          onClick={handleClick}
          onKeyDown={(e) => e.key === "Enter" && handleClick()}
          className={`h-full min-h-[3.5rem] w-full border-2 border-dashed rounded-lg flex items-center justify-center text-center transition-all group relative overflow-hidden ${value ? "border-slate-200 bg-slate-50" : "border-slate-200 hover:border-grigora-400 hover:bg-slate-50"
            } ${onUpload && !disabled ? "cursor-pointer" : "cursor-default"} ${uploading ? "opacity-70 pointer-events-none" : ""}`}
        >
          {value ? (
            <>
              <img src={value} alt="App icon" className="absolute inset-0 w-full h-full object-cover rounded-lg" />
              <span className="absolute bottom-0 left-0 right-0 text-[10px] text-slate-500 py-0.5 px-1.5 font-medium bg-white/90">Click to replace</span>
            </>
          ) : (
            <UploadCloud size={18} className="text-slate-400 group-hover:text-grigora-500" />
          )}
        </div>
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      </div>
    );
  }

  return (
    <div className="mb-4">
      {label && <label className="block text-slate-700 text-xs font-semibold mb-2 capitalize">{label}</label>}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
        className="hidden"
        onChange={handleChange}
      />
      <div
        role="button"
        tabIndex={onUpload && !disabled ? 0 : -1}
        onClick={handleClick}
        onKeyDown={(e) => e.key === "Enter" && handleClick()}
        className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center text-center transition-all group ${value ? "border-slate-200" : "border-slate-200 hover:border-grigora-400 hover:bg-slate-50"
          } ${onUpload && !disabled ? "cursor-pointer" : "cursor-default"} ${uploading ? "opacity-70 pointer-events-none" : ""}`}
      >
        {value ? (
          <div className="w-20 h-20 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0">
            <img src={value} alt="App icon" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 mb-3 group-hover:bg-white group-hover:text-grigora-500 shadow-sm">
            <UploadCloud size={20} />
          </div>
        )}
        {!value && (
          <>
            <p className="text-sm font-medium text-slate-700 mt-1">
              {onUpload ? (uploading ? "Uploading…" : "Click to upload icon") : "No icon"}
            </p>
            <p className="text-xs text-slate-400 mt-1">SVG, PNG, JPG, WebP (max 2MB)</p>
          </>
        )}
        {value && onUpload && !uploading && (
          <p className="text-xs text-slate-500 mt-2">Click to replace</p>
        )}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

/** Gallery for store listing: images and videos (max 10). value/onChange: array of { type: 'image'|'video', url }.
 * onUpload(appId, file, type) => Promise<{ url }> to upload to CDN. */
export function MediaManager({ label, value = [], onChange, disabled, onUpload, appId }) {
  const media = value || [];
  const [uploadingIndex, setUploadingIndex] = React.useState(null);
  const imageInputRef = React.useRef(null);
  const videoInputRef = React.useRef(null);

  const handleFile = async (file, type) => {
    if (!file || !onUpload || !appId || media.length >= 10) return;
    const index = media.length;
    setUploadingIndex(index);
    try {
      const result = await onUpload(appId, file, type);
      const url = result?.url;
      const resolvedType = result?.type || type;
      if (url) onChange?.([...media, { type: resolvedType, url }]);
    } catch (err) {
      console.error("Upload failed:", err);
      alert(err.response?.data?.error || err.message || "Upload failed.");
    } finally {
      setUploadingIndex(null);
      if (imageInputRef.current) imageInputRef.current.value = "";
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
  };

  const removeMedia = (index) => {
    onChange?.(media.filter((_, i) => i !== index));
  };

  const cellClass = "aspect-[4/3] min-h-0 w-full bg-slate-100 rounded-lg border border-slate-200 relative group overflow-hidden shadow-sm hover:shadow-md transition-all";
  const addCellClass = "aspect-[4/3] min-h-0 w-full border-2 border-dashed border-slate-200 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-grigora-400 hover:bg-slate-50 transition-all p-1.5 group bg-slate-50/30 overflow-hidden box-border";

  return (
    <div className="mb-6">
      <div className="flex justify-between items-end mb-3">
        {label && <label className="block text-slate-700 text-xs font-bold tracking-wider capitalize">{label}</label>}
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${media.length === 10 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
          {media.length} / 10 assets
        </span>
      </div>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file, "image");
        }}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file, "video");
        }}
      />
      <div className="min-h-[130px] w-full overflow-auto overflow-x-hidden rounded-xl border border-slate-200 bg-slate-50/50 p-3 box-border">
        <div className="grid grid-cols-5 gap-3">
          {media.map((item, index) => (
            <div key={index} className={cellClass}>
              <button
                type="button"
                onClick={() => removeMedia(index)}
                className="absolute top-1.5 right-1.5 p-1.5 bg-white/90 backdrop-blur rounded-full shadow-sm cursor-pointer opacity-0 group-hover:opacity-100 transition-all hover:bg-red-50 hover:text-red-500 z-10"
              >
                <X size={12} strokeWidth={2.5} />
              </button>
              {item.type === "video" && (
                <div className="absolute inset-0 flex items-center justify-center z-0 bg-black/20 pointer-events-none">
                  <div className="w-8 h-8 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center border border-white/20">
                    <Video size={14} className="text-white fill-current" />
                  </div>
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-slate-100 text-slate-300">
                {item.type === "image" && item.url ? (
                  <img src={item.url} alt="" className="w-full h-full object-cover" />
                ) : item.type === "video" && item.url ? (
                  <video src={item.url} className="w-full h-full object-cover" muted playsInline />
                ) : (
                  item.type === "image" ? <ImageIcon size={24} /> : <Video size={24} />
                )}
              </div>
            </div>
          ))}
          {uploadingIndex !== null && (
            <div key="uploading" className={cellClass}>
              <div className="absolute inset-0 flex items-center justify-center bg-slate-200/80 z-10">
                <span className="text-xs font-medium text-slate-600">Uploading…</span>
              </div>
            </div>
          )}
          {media.length < 10 && !disabled && uploadingIndex === null && (
            <div className={addCellClass}>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  className="w-6 h-6 flex items-center justify-center bg-white border border-slate-200 rounded-md text-slate-400 hover:text-grigora-600 hover:border-grigora-300 hover:shadow-sm transition-all"
                  title="Add Image"
                >
                  <ImageIcon size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => videoInputRef.current?.click()}
                  className="w-6 h-6 flex items-center justify-center bg-white border border-slate-200 rounded-md text-slate-400 hover:text-grigora-600 hover:border-grigora-300 hover:shadow-sm transition-all"
                  title="Add Video"
                >
                  <Video size={12} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-2 flex items-center">
        <span className="w-1 h-1 rounded-full bg-slate-300 mr-2" />
        Upload up to 10 images or videos. Images: PNG, JPG, WebP, GIF. Videos: MP4, WebM.
      </p>
    </div>
  );
}

export function Modal({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
          <h3 className="font-bold text-lg text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
